import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema, type LoginInput } from '@plane/shared';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { Field } from '@/components/field';
import { useAuth } from '@/modules/auth/hooks/auth-context';
import { ApiRequestError } from '@/services/http-client';

/** Mensagem para a pessoa, a partir do código estável que a API devolve. */
function mensagemDeErro(erro: unknown): string {
  if (!(erro instanceof ApiRequestError)) {
    return 'Não foi possível falar com o servidor. Verifique a conexão e tente de novo.';
  }

  switch (erro.code) {
    case 'INVALID_CREDENTIALS':
      // Igual para e-mail inexistente e senha errada, como a API responde:
      // dizer qual dos dois falhou revelaria quais e-mails têm conta.
      return 'E-mail ou senha incorretos.';
    case 'TOO_MANY_REQUESTS':
      return erro.message;
    default:
      return erro.message;
  }
}

/** Tela de acesso ao painel. */
export function LoginPage() {
  const { usuario, carregando, entrar } = useAuth();
  const navigate = useNavigate();
  const localizacao = useLocation();
  const [erro, setErro] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const destino = (localizacao.state as { de?: string } | null)?.de ?? '/';

  // Quem já tem sessão não vê o formulário — inclusive ao voltar pelo histórico
  // depois de entrar.
  if (!carregando && usuario) return <Navigate to={destino} replace />;

  const onSubmit = handleSubmit(async (valores) => {
    setErro(null);

    try {
      await entrar(valores);
      void navigate(destino, { replace: true });
    } catch (falha) {
      setErro(mensagemDeErro(falha));
    }
  });

  return (
    <form onSubmit={onSubmit} className="flex w-full max-w-sm flex-col gap-5" noValidate>
      <div>
        <p className="text-xs tracking-[0.18em] text-brass uppercase">Painel</p>
        <h1 className="mt-2 font-display text-3xl">
          Plane <span className="italic text-brand-600">Móveis</span>
        </h1>
      </div>

      {erro && (
        <p role="alert" className="border border-brass bg-bone-soft px-3 py-2.5 text-sm text-ink">
          {erro}
        </p>
      )}

      <Field
        id="email"
        type="email"
        label="E-mail"
        autoComplete="username"
        error={errors.email?.message}
        {...register('email')}
      />

      <Field
        id="password"
        type="password"
        label="Senha"
        autoComplete="current-password"
        error={errors.password?.message}
        {...register('password')}
      />

      <button
        type="submit"
        disabled={isSubmitting}
        className="bg-brand-600 px-5 py-3 text-sm text-bone transition-colors hover:bg-brand-700 disabled:opacity-60"
      >
        {isSubmitting ? 'Entrando…' : 'Entrar'}
      </button>
    </form>
  );
}
